import { useState } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge, TriggerStatusBadge } from "@/components/admin/XeroRunDisplay";
import { useXeroOrgName } from "@/hooks/useXeroOrgName";
import { REASON_LABEL, REASON_TONE, isNoteworthy } from "@/lib/xeroReasonLabels";
import { formatNumber, formatWhen } from "@/lib/xeroRunFormat";
import { type XeroPipelineRun } from "@/services/xeroApi";

const MODE_LABEL: Record<XeroPipelineRun["mode"], string> = {
  incremental: "Incremental",
  backfill: "Backfill",
};

interface Props {
  run: XeroPipelineRun;
}


export default function XeroLastRunSummary({ run }: Props) {
  const [open, setOpen] = useState(false);
  const orgName = useXeroOrgName();
  const { summary } = run;

  // Everything the run has to say about an organisation, reported rows and
  // reconciled absences together, so the modal is one table rather than two.
  const rows = [
    ...run.organisations.map((org) => ({
      tenant_id: org.tenant_id,
      name: orgName(org.tenant_id, org.tenant_name),
      status: org.status,
      reason: org.reason,
      detail: org.error_message,
      rows_ingested: org.rows_ingested,
      pages_fetched: org.pages_fetched,
      watermark: org.watermark_journal_number,
    })),
    ...run.unreported_organisations.map((org) => ({
      tenant_id: org.tenant_id,
      name: orgName(org.tenant_id, org.tenant_name),
      status: null,
      reason: org.reason,
      detail: null,
      rows_ingested: 0,
      pages_fetched: 0,
      watermark: null,
    })),
  ];

  const noteworthy = rows.filter((row) => isNoteworthy(row.reason)).length;
  const quiet = summary.skipped + summary.pending;

  return (
    <section className="space-y-3 rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <TriggerStatusBadge status={run.status} />
            <span className="text-xs text-muted-foreground">
              {MODE_LABEL[run.mode]} · {formatWhen(run.finished_at ?? run.triggered_at)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatNumber(summary.granted || summary.organisations)} organisations ·{" "}
            {formatNumber(summary.succeeded)} loaded
            {quiet > 0 && <> · {formatNumber(quiet)} reported nothing</>}
            {summary.failed > 0 && (
              <span className="text-destructive"> · {formatNumber(summary.failed)} failed</span>
            )}
            {summary.not_attempted > 0 && (
              <span className="text-amber-700">
                {" "}
                · {formatNumber(summary.not_attempted)} not attempted
              </span>
            )}
          </p>
        </div>

        {/* Only offered when there is something to explain. A clean run is the
            summary line and nothing else. */}
        {noteworthy > 0 && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-2">
            <Info className="h-4 w-4" />
            More detail
          </Button>
        )}
      </div>

      {summary.stopped_early && (
        <p className="text-xs text-amber-700">
          An organisation spent its daily Xero call budget, so the run stopped before
          reaching the rest. The next run resumes from each watermark.
        </p>
      )}

      {run.reconcile_error && (
        <p className="text-xs text-muted-foreground">{run.reconcile_error}</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Organisations in the last run</DialogTitle>
            <DialogDescription>
              {MODE_LABEL[run.mode]} run, {formatWhen(run.triggered_at)}. Every reason is
              reported by the notebook as it closes each organisation.
            </DialogDescription>
          </DialogHeader>

          {/* Plain overflow, not Radix ScrollArea: see the note in XeroPanel. */}
          <div className="max-h-[60vh] overflow-auto rounded-lg border border-border">
            <div>
              <Table>
                <TableHeader>
                  <TableRow className="sticky top-0 z-10 bg-muted hover:bg-muted">
                    <TableHead>Organisation</TableHead>
                    <TableHead className="w-[110px]">Status</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="w-[90px] text-right">Rows</TableHead>
                    <TableHead className="w-[80px] text-right">Pages</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.tenant_id}>
                      <TableCell className="align-top font-medium text-foreground">
                        {row.name}
                      </TableCell>
                      <TableCell className="align-top">
                        <StatusBadge status={row.status} />
                      </TableCell>
                      <TableCell className="align-top">
                        <span
                          className={
                            row.reason ? REASON_TONE[row.reason] ?? "text-foreground" : ""
                          }
                        >
                          {row.reason ? REASON_LABEL[row.reason] : "—"}
                        </span>
                        {row.detail && (
                          <p className="mt-1 text-xs text-destructive">{row.detail}</p>
                        )}
                      </TableCell>
                      <TableCell className="align-top text-right text-muted-foreground">
                        {formatNumber(row.rows_ingested)}
                      </TableCell>
                      <TableCell className="align-top text-right text-muted-foreground">
                        {formatNumber(row.pages_fetched)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
